import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import React, { FC, useCallback, useState } from 'react'

import { useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { LinkedProductItem } from '@Domains/Product/Features/Display/LinkedProductItem/LinkedProductItem'
import { ProductSidePaneLayout } from '@Domains/Product/Features/Display/ProductSidePaneLayout/ProductSidePaneLayout'
import { LinkedProduct } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

import styles from './LinkedProductList.module.scss'

export interface LinkedProductsProps {
  defaultVariantId: string
  products: LinkedProduct[]
  type: string
}

export const LinkedProductList: FC<LinkedProductsProps> = ({ defaultVariantId, products, type }) => {
  const { t } = useTranslation()
  const [variantId, setVariantId] = useState(defaultVariantId)
  const ref = useNativeModalActions()

  const resetVariantId = () => setVariantId(defaultVariantId)

  const getTitle = useCallback(() => {
    switch (type) {
      case 'color':
        return t('products:WB_CHOOSE_COLOUR')
      case 'size':
        return t('products:SIZES')
      case 'variation':
        return t('products:VARIATIONS')
      default:
        return type
    }
  }, [t, type])

  const getVariantTitle = () => {
    const activeProduct = products.find(product => product.productID === variantId)

    if (activeProduct) {
      return ` - ${activeProduct.title}`
    }

    return ''
  }

  const shownProducts = sortBy(products, x => x.productID !== defaultVariantId)

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        {getTitle()}
        {getVariantTitle()}
      </div>

      <div className={`${styles.list} ${shownProducts.length > 3 ? styles.full : ''}`}>
        {map(shownProducts, (product, index) => (
          <LinkedProductItem
            key={product.productID}
            className={index > 2 ? styles.hiddenDesktop : ''}
            image={product.image}
            productID={product.productID}
            resetVariantId={resetVariantId}
            selected={variantId === product.productID}
            setSelected={setVariantId}
            title={product.title}
            type={type}
            url={product.url}
          />
        ))}

        {shownProducts.length > 3 && (
          <button className={styles.more} onClick={() => ref.current?.open()} type="button">
            {`+${shownProducts.length - 3}`}
          </button>
        )}
      </div>

      <ProductSidePaneLayout
        actionRef={ref}
        title={getTitle()}
      >
        <div className={styles.sidepaneTitle}>
          {getTitle()}
          {getVariantTitle()}
        </div>
        <div className={styles.sidepaneProducts}>
          {map(shownProducts, product => (
            <LinkedProductItem
              key={product.url}
              className={styles.sidepaneProduct}
              image={product.image}
              productID={product.productID}
              resetVariantId={resetVariantId}
              selected={variantId === product.productID}
              setSelected={setVariantId}
              title={product.title}
              type={type}
              url={product.url}
            />
          ))}
        </div>
      </ProductSidePaneLayout>

    </div>
  )
}

